import TestService from '@boServices/testService';
import { useCallback, useEffect, useRef, useState } from 'react';

const testService = new TestService();

// Values rebuilt from a comma-separated key.
const idsOf = (key) => (key ? key.split(',') : []);

const useTests = ({ search, page, perPage, statuses = [], languageIds = [], levelIds = [], sortBy }) => {
    // The caller rebuilds the arrays on every render; depend on their content instead
    // of their identity, and rebuild them from those keys inside the effect.
    const statusesKey = statuses.join(',');
    const languageIdsKey = languageIds.join(',');
    const levelIdsKey = levelIds.join(',');
    const [tests, setTests] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);

    const fetchTests = useCallback(() => {
        // A newer request cancels the previous one, whose late result must not overwrite the current list.
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        return testService
            .getTests({
                search,
                page,
                perPage,
                statuses: idsOf(statusesKey),
                languageIds: idsOf(languageIdsKey),
                levelIds: idsOf(levelIdsKey),
                sortBy,
                signal: controller.signal,
            })
            .then((data) => {
                if (controller.signal.aborted) return;
                setTests(data.items ?? []);
                setTotal(data.total ?? 0);
                setError(null);
            })
            .catch((err) => {
                if (!controller.signal.aborted) setError(err);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
    }, [search, page, perPage, statusesKey, languageIdsKey, levelIdsKey, sortBy]);

    useEffect(() => {
        fetchTests();
        return () => abortRef.current?.abort();
    }, [fetchTests]);

    return { tests, total, loading, error };
};

export default useTests;
